/**
 * D348: the farm theme's map kit (`MapKit` in `iso-kit.ts`), the first theme, drawn exactly as
 * the farm was before themes. Its producers' plots and chain buildings (moved here from
 * `idle/map.ts`), the country round it (D329), its palette and light (`iso.ts`,
 * `idle/appearance.ts`), and the flat map's painters (`iso-paint.ts`, and the country painted
 * here). Its 3D half is `farmThree` in `idle/three/farm.ts`.
 *
 * Adding a producer to the farm's map: give it a slot in `SLOTS` below (where its land starts,
 * its size and what is drawn on it: `kind`, plus `crop`, `animal`, `mud`, `hives`, `vines`,
 * `hops`, `crocus` or `house`); a chain's building goes in `CHAIN_BUILDINGS`. Keep it clear of
 * the yard (y 0 to 5, x 0 to 5; D440) and of the festival marks' row (y 0 to 1 from x 6 east). A new
 * kind of thing standing on the land is a painter in `iso-paint.ts`, called from `propsFor`.
 */
import { getIdleContent } from '@hedgerow/data';
import type { CountryItem, WorldLayout } from '../idle/map.ts';
import {
  ASSET_PALETTE,
  SMOKE,
  SNOW,
  COUNTRY_TREE,
  GOOD_WEATHER,
  LANDSCAPE,
  LANDSCAPE_CROP,
  PHASE_LIGHT,
  PHASE_TIME,
  FIELD_CYCLE,
  READY_BADGE,
  STREAM,
  countryTreeKind,
} from '../idle/appearance.ts';
import { THEME_MAP_COLOURS } from '../idle/assets/idle-worlds-assets.js';
import type { IsoBuildingAt } from './IsoWorldMap.tsx';
import {
  BASE,
  TW,
  alpha,
  assetLevel,
  frontCorner,
  heartPlan,
  isPlan,
  mix,
  palette,
  polyPath,
  project,
  rectPts,
  rnd,
  shade,
  tint,
  unionBounds,
  type Bounds,
  type Heart,
  type IsoParcel,
  type Palette,
  type Point,
  fameProgress,
  WINTER_COVER,
} from './iso.ts';
import {
  countryLeaf,
  paintBoundaries,
  paintFarmGate,
  paintFieldJob,
  paintHedge,
  paintMachine,
  paintParcel,
  paintProp,
  pondRipples,
  propsFor,
  smokePuffs,
  standingHeight,
  type Prop,
  type Puff,
  type Ripple,
} from './iso-paint.ts';
import { GATE_DECOR, HEDGE_DECOR, TRACK } from '../idle/appearance.ts';
import type { FlatEdges, FlatScenery, MapKit, PlotSlot } from './iso-kit.ts';

/** D440: the theme's accent and money colours over the game's palette, as the 3D map takes them. */
const FARM_PALETTE = { ...ASSET_PALETTE, ...THEME_MAP_COLOURS.farm };

/* ---------- the farm's plots and chain buildings (D259, D267, D271, D302, D343) ---------- */

/** The land kept for each producer. Producers with no slot are not drawn yet. */
export const SLOTS: Record<string, PlotSlot> = {
  // D343: `chainReserve` below holds back the tiles each producer's own buildings (D271's
  // and D343's) stand on, from the plot's own front corner inward, so the crop never grows
  // under them; see `CHAIN_BUILDINGS` for exactly where each stands in that reserve.
  wheat: { x: 6, y: 2, w: 5, h: 4, kind: 'field', crop: 'wheat', chainReserve: 1 },
  carrots: { x: 0, y: 6, w: 4, h: 4, kind: 'field', crop: 'carrots', chainReserve: 1 },
  hens: {
    x: 4,
    y: 6,
    w: 4,
    h: 4,
    kind: 'pasture',
    animal: 'hen',
    house: { x: 6, y: 6, w: 2, h: 2, kind: 'shed', use: 'livestock' },
    chainReserve: 1,
  },
  potatoes: { x: 11, y: 2, w: 3, h: 6, kind: 'field', crop: 'potatoes', chainReserve: 1 },
  apples: { x: 8, y: 6, w: 3, h: 6, kind: 'orchard' },
  cows: { x: 11, y: 8, w: 3, h: 5, kind: 'pasture', animal: 'cow', chainReserve: 1 },
  bees: { x: 0, y: 10, w: 3, h: 3, kind: 'meadow', hives: true, chainReserve: 1 },
  // D343: the winery needs a clean 2x2 corner, which only a five-tile reserve guarantees on
  // a 3x3 plot (the sixth-from-last tile falls outside that corner and is simply never grown).
  vines: { x: 3, y: 10, w: 3, h: 3, kind: 'orchard', vines: true, chainReserve: 5 },
  // D251: the four that open after festivals. D440: the default plan's yard is 6 by 6, so five
  // plots are turned (potatoes, apples, sheep, cows, saffron), the positions as in the data.
  pigs: {
    x: 0,
    y: 13,
    w: 5,
    h: 3,
    kind: 'pasture',
    animal: 'pig',
    mud: true,
    house: { x: 0, y: 13, w: 2, h: 2, kind: 'shed', use: 'livestock' },
    chainReserve: 1,
  },
  sheep: { x: 5, y: 14, w: 3, h: 5, kind: 'pasture', animal: 'sheep', chainReserve: 1 },
  hops: { x: 6, y: 10, w: 2, h: 4, kind: 'orchard', hops: true, chainReserve: 1 },
  saffron: { x: 8, y: 12, w: 3, h: 4, kind: 'meadow', crocus: true, chainReserve: 1 },
  // D300: the five producers added that generation, along the farm's new back row.
  // D343: barley carries two buildings, the maltings and (after it) the distillery, so its
  // reserve is four tiles: the exact back-row-and-one corner `blockTiles` holds back last.
  barley: { x: 11, y: 13, w: 3, h: 3, kind: 'field', crop: 'barley', chainReserve: 4 },
  hay: { x: 0, y: 16, w: 3, h: 3, kind: 'field', crop: 'hay', chainReserve: 1 },
  rapeseed: { x: 8, y: 16, w: 3, h: 3, kind: 'field', crop: 'rapeseed', chainReserve: 1 },
  strawberries: { x: 11, y: 16, w: 3, h: 3, kind: 'field', crop: 'strawberries', chainReserve: 1 },
  tomatoes: { x: 3, y: 16, w: 2, h: 3, kind: 'orchard', glasshouse: true, chainReserve: 2 },
  // D301: the water meadow, on its own piece of land beside the farm, open once a Lineage
  // trait opens it. Placed by `idleLayout`; these are the producers' own plots within it.
  watercress: {
    x: 0,
    y: 0,
    w: 3,
    h: 4,
    kind: 'field',
    crop: 'watercress',
    land: 'water_meadow',
    chainReserve: 1,
  },
  trout: { x: 3, y: 0, w: 3, h: 4, kind: 'pond', land: 'water_meadow', chainReserve: 1 },
};

/**
 * Where each chain's building stands. The three D271 chains stand beside the producer they
 * lift; every other chain (D302's and D343's sixteen) stands in its own plot's `chainReserve`,
 * its own tiles never grown over. D343: a crop with two buildings splits its reserve between
 * them, each a plain rectangle that exactly fits the tiles `chainReserve` actually holds back
 * (checked against `blockTiles`'/`slotTiles`' own fill order, not just counted).
 */
export const CHAIN_BUILDINGS: Record<string, IsoBuildingAt> = {
  mill: { x: 0, y: 3, w: 1, h: 2, kind: 'silo', use: 'processing' },
  dairy: { x: 5, y: 10, w: 1, h: 2, kind: 'shed', use: 'processing' },
  // D251: the cider press, at the end of the orchard.
  press: { x: 12, y: 7, w: 2, h: 2, kind: 'shed', use: 'processing' },
  // D302: oil press on rapeseed, woollen mill on sheep, brewery on hops.
  oil_press: { x: 8, y: 18, w: 1, h: 1, kind: 'shed', use: 'processing' },
  woollen_mill: { x: 9, y: 15, w: 1, h: 1, kind: 'shed', use: 'processing' },
  brewery: { x: 13, y: 12, w: 1, h: 1, kind: 'shed', use: 'processing' },
  // D343: one real building per crop, standing on the tile (or two) its own `chainReserve`
  // holds back.
  maltings: { x: 0, y: 18, w: 2, h: 1, kind: 'silo', use: 'processing' },
  distillery: { x: 2, y: 17, w: 1, h: 2, kind: 'silo', use: 'processing' },
  crisp_works: { x: 5, y: 9, w: 1, h: 1, kind: 'shed', use: 'processing' },
  veg_box_shed: { x: 9, y: 6, w: 1, h: 1, kind: 'shed', use: 'processing' },
  cress_packhouse: { x: 2, y: 3, w: 1, h: 1, kind: 'shed', use: 'processing' },
  feed_store: { x: 5, y: 18, w: 1, h: 1, kind: 'silo', use: 'processing' },
  egg_packing_shed: { x: 13, y: 6, w: 1, h: 1, kind: 'shed', use: 'processing' },
  smokehouse: { x: 4, y: 15, w: 1, h: 1, kind: 'shed', use: 'processing' },
  trout_smokery: { x: 5, y: 3, w: 1, h: 1, kind: 'shed', use: 'processing' },
  jam_kitchen: { x: 11, y: 18, w: 1, h: 1, kind: 'shed', use: 'processing' },
  honey_house: { x: 8, y: 12, w: 1, h: 1, kind: 'shed', use: 'processing' },
  packhouse: { x: 12, y: 18, w: 2, h: 1, kind: 'shed', use: 'processing' },
  bakery: { x: 5, y: 6, w: 1, h: 1, kind: 'shed', use: 'processing' },
  winery: { x: 10, y: 11, w: 2, h: 2, kind: 'shed', use: 'processing' },
  creamery: { x: 4, y: 12, w: 1, h: 1, kind: 'shed', use: 'processing' },
  drying_shed: { x: 13, y: 15, w: 1, h: 1, kind: 'shed', use: 'processing' },
};

/* ---------- D329: the country beyond the fence ---------- */

const overlaps = (a: Bounds, b: Bounds): boolean =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

let landscapeCache: { key: string; out: CountryItem[] } | null = null;

/**
 * D329, D429: the country round the farm, from its seed: the lane out from the farm gate, the
 * hills a kind brings, and where the asset module's country walks from (`fscene`), with what it
 * keeps clear of: each piece of land beside the farm (`fland`), the river and the lane
 * (`fkeep`). Its fields, hedges, trees, cottages and village are `farmScenery`'s. Pure: the
 * same farm gives the same country on every device.
 */
export function landscapeFor(L: WorldLayout, plot: Bounds, lands: Bounds[]): CountryItem[] {
  const key = JSON.stringify([L.seed, plot, lands, L.river.length, L.gate, L.scenery]);
  const sc = L.scenery;
  if (landscapeCache?.key === key) return landscapeCache.out;
  const S = L.seed >>> 0;
  const R = LANDSCAPE.reach,
    gap = LANDSCAPE.gap;
  const area: Bounds = { x: plot.x - R, y: plot.y - R, w: plot.w + 2 * R, h: plot.h + 2 * R };
  const pad = (b: Bounds, n: number): Bounds => ({
    x: b.x - n,
    y: b.y - n,
    w: b.w + 2 * n,
    h: b.h + 2 * n,
  });
  const keep: Bounds[] = [pad(plot, gap), ...lands.map((b) => pad(b, gap))];
  for (const t of L.river) keep.push({ x: t.x - 1, y: t.y - 1, w: 3, h: 3 });
  // The lane: the track's own run from the plot's edge to the gate, then on out to the edge.
  for (const q of L.lane)
    if (!overlaps({ x: q.x - 0.5, y: q.y - 0.5, w: 1, h: 1 }, plot))
      keep.push({ x: q.x - 1, y: q.y - 1, w: 2, h: 2 });
  const g = L.gate;
  const dir: [number, number] = g.y >= L.height ? [0, 1] : g.x <= 0 ? [-1, 0] : [1, 0];
  const far = R + Math.max(plot.w, plot.h);
  const laneBox: Bounds =
    dir[1] === 0
      ? { x: dir[0] < 0 ? g.x - far : g.x, y: g.y - 1, w: far, h: 2 }
      : { x: g.x - 1, y: g.y, w: 2, h: far };
  keep.push(laneBox);
  const out: CountryItem[] = [];
  // D344: a kind's hills, as a scale on the 3D country's rise (`s`), placed off the land.
  if (sc.hills !== 1) out.push({ t: 'chills', x: area.x, y: area.y, w: 0, h: 0, s: sc.hills });
  const half = LANDSCAPE.lane.half;
  out.push(
    dir[1] === 0
      ? { t: 'clane', x: laneBox.x, y: g.y - half, w: laneBox.w, h: half * 2 }
      : { t: 'clane', x: g.x - half, y: g.y, w: half * 2, h: laneBox.h },
  );

  // D429: the neighbours' fields, hedges, trees, cottages and the village are the asset
  // module's country (`farmScenery`), walked from here and kept clear of the land, the lands
  // beside it, the river and the lane.
  out.push(
    // Each with the strip of grass round it, so none stands on the land itself.
    { t: 'fscene', ...pad(plot, 1), r: 1 + (S % 9973) },
    ...lands.map((b) => ({ t: 'fland', ...pad(b, 1) })),
    // The river's tiles and the lane's, each with the margin it keeps (`s`), and the lane out.
    ...L.river.map((t) => ({ t: 'fkeep', x: t.x, y: t.y, w: 1, h: 1, s: 1 })),
    ...L.lane
      .filter((q) => !overlaps({ x: q.x - 0.5, y: q.y - 0.5, w: 1, h: 1 }, plot))
      .map((q) => ({ t: 'fkeep', x: q.x - 0.5, y: q.y - 0.5, w: 1, h: 1, s: 0.5 })),
    { t: 'fkeep', ...laneBox, s: 0 },
  );
  landscapeCache = { key, out };
  return out;
}

/** D429: how far the asset module's country reaches past the land, in tiles. */
export const SCENERY_REACH = LANDSCAPE.haze.far / 2;

/** D429: the module's field colours as the country's crops: pale grass, gold, green, wheat. */
const PATCH_CROP = ['pasture', 'barley', 'beans', 'wheat'] as const;

/**
 * D429: where the asset module's country keeps clear: the land and a strip round it, the
 * lands beside it, the river and the lane. Both maps ask it of the same layout.
 */
export function farmSceneryClear(items: readonly CountryItem[]): (x: number, z: number) => boolean {
  const keep: Bounds[] = items
    .filter((q) => q.t === 'fscene' || q.t === 'fland' || q.t === 'fkeep')
    .map((q) => {
      const n = q.s ?? 0;
      return { x: q.x - n, y: q.y - n, w: q.w + 2 * n, h: q.h + 2 * n };
    });
  return (x, z) => !keep.some((b) => x >= b.x && x <= b.x + b.w && z >= b.y && z <= b.y + b.h);
}

/**
 * D429: how far the world has grown, for the module's country: the producers on the map over
 * all the farm's producers, 0 with the first and 1 with every one.
 */
export function farmSceneryProgress(parcels: readonly IsoParcel[] | undefined): number {
  const total = getIdleContent('farm').producers.length;
  const n = new Set((parcels ?? []).filter((p) => p.id).map((p) => p.id)).size;
  return total > 1 ? Math.min(1, Math.max(0, n - 1) / (total - 1)) : 1;
}

/**
 * D429: the asset module's country (`scenery('farm', …)`) as both maps place it: the same
 * seeded walk over the same cells. Each cell's field (`cfield`, its crop from the module's
 * colour) is painted on the country's ground; its hedge clumps (`fhedge`, each a 0.6 run),
 * trees (`ftree`) and cottages (`fhouse`) and the village on the hill (`fvillage`, `s` the
 * world's growth) stand where the 3D map's module pieces do, kept clear as `farmSceneryClear`.
 */
export function farmScenery(items: readonly CountryItem[], progress: number): CountryItem[] {
  const walk = items.find((q) => q.t === 'fscene');
  if (!walk) return [];
  let seed = walk.r ?? 1;
  const r = () => ((seed = (seed * 16807) % 2147483647) - 1) / 2147483646;
  const clear = farmSceneryClear(items);
  const out: CountryItem[] = [];
  // The walk's own bounds are the land's, inside the strip `fscene` carries.
  const x0 = walk.x + 1,
    z0 = walk.y + 1,
    x1 = walk.x + walk.w - 1,
    z1 = walk.y + walk.h - 1,
    m = SCENERY_REACH;
  const X0 = x0 - m,
    Z0 = z0 - m,
    X1 = x1 + m;
  const cell = 3,
    near = cell * 0.6;
  // A hedge as the module's `hedgeRun` lays it: a clump every 0.6, each kept or not on its own.
  const hedge = (ax: number, az: number, bx: number, bz: number) => {
    const n = Math.max(2, Math.round(Math.hypot(bx - ax, bz - az) / 0.6));
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n;
      const x = ax + (bx - ax) * t,
        z = az + (bz - az) * t;
      if (clear(x, z)) out.push({ t: 'fhedge', x, y: z, w: (bx - ax) / n, h: (bz - az) / n });
    }
  };
  for (let x = X0; x < X1; x += cell)
    for (let z = Z0; z < z1 + m; z += cell) {
      const cx = x + cell / 2,
        cz = z + cell / 2;
      if (cx > x0 - near && cx < x1 + near && cz > z0 - near && cz < z1 + near) continue;
      const k = r();
      const crop = PATCH_CROP[Math.floor(r() * 4)] ?? 'pasture';
      out.push({
        t: 'cfield',
        k: crop,
        x: x + 0.15,
        y: z + 0.15,
        w: cell - 0.3,
        h: cell - 0.3,
        r: 0,
      });
      hedge(x, z + 0.05, x + cell, z + 0.05);
      if (r() < 0.4) hedge(x + 0.05, z, x + 0.05, z + cell);
      if (k < 0.25)
        for (let i = 0; i < 3; i++) {
          const tx = cx - 0.8 + r() * 1.6,
            tz = cz - 0.8 + r() * 1.6;
          if (clear(tx, tz)) out.push({ t: 'ftree', x: tx, y: tz, w: 0, h: 0, s: 1.1 });
        }
      else if (k < 0.3 && clear(cx, cz)) out.push({ t: 'fhouse', x: cx, y: cz, w: 0.9, h: 0.6 });
    }
  const vx = X0 + (X1 - X0) * 0.3,
    vz = Z0 + 2.5;
  if (clear(vx, vz)) out.push({ t: 'fvillage', x: vx, y: vz, w: 0, h: 0, s: progress });
  return out;
}

/* ---------- the flat map's country, edges and weather ---------- */

/**
 * The country, hazed back so the plot carries the eye, sized off the plot rather than off a
 * fixed square, because a farm grows to 40 tiles a side; the river on it (D267, D287); and
 * the seeded trees round the farm.
 */
function paintCountry(s: FlatScenery, P: Palette, o: string[]): void {
  const { far, stream, lane, season } = s;
  // D429: the asset module's country, walked as the 3D map walks it.
  // D430: grown with the world's years and generations, as the module grows it.
  const progress = fameProgress(
    farmSceneryProgress(s.parcels),
    s.age ?? { years: 0, generations: 0 },
  );
  const country = [...s.country, ...farmScenery(s.country, progress)];
  o.push(
    `<path d="${polyPath(rectPts(far.x - 18, far.y - 18, far.w + 36, far.h + 36))}" fill="${P.far(P.meadow)}"/>`,
  );
  /* D329: the neighbours' land, lighter than the 3D farm's: each field in its crop's
             colour for the season, the lane, the hedges and their trees, out to the map's
             edge and no further. */
  const R = LANDSCAPE.flatReach;
  const edge = {
    x0: far.x - R,
    y0: far.y - R,
    x1: far.x + far.w + R,
    y1: far.y + far.h + R,
  };
  const clip = (c: { x: number; y: number; w: number; h: number }) => {
    const x0 = Math.max(c.x, edge.x0),
      y0 = Math.max(c.y, edge.y0),
      x1 = Math.min(c.x + c.w, edge.x1),
      y1 = Math.min(c.y + c.h, edge.y1);
    return x1 >= x0 && y1 >= y0 ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : null;
  };
  const seasonKey = (
    ['spring', 'summer', 'autumn', 'winter'].includes(String(season)) ? season : 'summer'
  ) as 'spring' | 'summer' | 'autumn' | 'winter';
  for (const c of country) {
    if (c.t !== 'cfield' && c.t !== 'clane') continue;
    const b = clip(
      c.t === 'cfield' ? { x: c.x + 0.15, y: c.y + 0.15, w: c.w - 0.3, h: c.h - 0.3 } : c,
    );
    if (!b) continue;
    const look = LANDSCAPE_CROP[c.k ?? 'pasture'] ?? LANDSCAPE_CROP.pasture;
    // D440: winter's snow lies on the neighbours' fields too.
    const crop = mix(
      look?.[seasonKey] ?? P.pasture,
      SNOW.colour,
      WINTER_COVER.field * (P.snow ?? 0),
    );
    const fill = c.t === 'clane' ? P.far(P.road) : P.far(crop);
    o.push(`<path d="${polyPath(rectPts(b.x, b.y, b.w, b.h))}" fill="${fill}"/>`);
    // A few rows across a sown field, so it reads as a crop and not a patch of colour.
    if (c.t === 'cfield' && (look?.look === 'rows' || look?.look === 'furrow')) {
      const along = c.r === 1;
      const n = Math.floor((along ? b.w : b.h) / 0.75);
      for (let i = 1; i < n; i++) {
        const t = i * 0.75;
        const [p1x, p1y] = project(along ? b.x + t : b.x, along ? b.y : b.y + t),
          [p2x, p2y] = project(along ? b.x + t : b.x + b.w, along ? b.y + b.h : b.y + t);
        o.push(
          `<line x1="${p1x.toFixed(0)}" y1="${p1y.toFixed(0)}" x2="${p2x.toFixed(0)}" y2="${p2y.toFixed(0)}" stroke="${alpha(shade(fill, 0.2), 0.5)}" stroke-width="1.2"/>`,
        );
      }
    }
  }
  for (const c of country) {
    if (c.t === 'fhedge') {
      // D429: one of the module's hedge clumps, as a short run of hedge.
      if (!clip({ x: c.x, y: c.y, w: 0, h: 0 })) continue;
      const [x1, y1] = project(c.x - c.w * 0.6, c.y - c.h * 0.6),
        [x2, y2] = project(c.x + c.w * 0.6, c.y + c.h * 0.6);
      o.push(
        `<line x1="${x1.toFixed(0)}" y1="${y1.toFixed(0)}" x2="${x2.toFixed(0)}" y2="${y2.toFixed(0)}" stroke="${shade(P.far(P.hedge), 0.1)}" stroke-width="5" stroke-linecap="round"/>`,
      );
      continue;
    }
    if (c.t !== 'chedge') continue;
    const b = clip(c);
    if (!b) continue;
    const [x1, y1] = project(b.x, b.y),
      [x2, y2] = project(b.x + b.w, b.y + b.h);
    o.push(
      `<line x1="${x1.toFixed(0)}" y1="${y1.toFixed(0)}" x2="${x2.toFixed(0)}" y2="${y2.toFixed(0)}" stroke="${shade(P.far(P.hedge), 0.1)}" stroke-width="5" stroke-linecap="round"/>`,
    );
  }
  for (const c of country) {
    if ((c.t !== 'chtree' && c.t !== 'ftree') || !clip({ x: c.x, y: c.y, w: 0, h: 0 })) continue;
    const [cx, cy] = project(c.x, c.y);
    const r = 6 * (c.s ?? 1);
    // D381: the kind of tree as the 3D country has it: a dark spire of a conifer, a birch's
    // lighter crown, or an oak. D429: the module's country trees are round, as oaks.
    const kind = c.t === 'ftree' ? 'oak' : countryTreeKind(c.x, c.y, c.k === 'hedge');
    const leaf = countryLeaf(P, kind, 0.5);
    const T = COUNTRY_TREE.tone;
    if (kind === 'conifer') {
      const h = r * 2.6;
      o.push(
        `<path d="M${(cx - r * 0.6).toFixed(1)} ${(cy + 1).toFixed(1)}L${cx.toFixed(1)} ${(cy - h).toFixed(1)}L${(cx + r * 0.6).toFixed(1)} ${(cy + 1).toFixed(1)}Z" fill="${shade(leaf, T.dark)}"/>`,
      );
    } else if (kind === 'birch')
      o.push(
        `<ellipse cx="${cx.toFixed(1)}" cy="${(cy - r * 0.9).toFixed(1)}" rx="${(r * 0.65).toFixed(1)}" ry="${(r * 0.95).toFixed(1)}" fill="${shade(leaf, T.mid)}"/>`,
      );
    else
      o.push(
        `<circle cx="${(cx + 2).toFixed(1)}" cy="${(cy - r * 0.6 + 3).toFixed(1)}" r="${r.toFixed(1)}" fill="${shade(leaf, T.dark)}"/>` +
          `<circle cx="${cx.toFixed(1)}" cy="${(cy - r * 0.6).toFixed(1)}" r="${r.toFixed(1)}" fill="${shade(leaf, T.mid)}"/>`,
      );
  }
  // D429: the module's cottages and the village on the hill.
  for (const c of country) {
    if (c.t === 'fhouse' && clip({ x: c.x, y: c.y, w: 0, h: 0 }))
      paintCottage(c.x, c.y, 0.9, 0.6, 0.45, P, o);
    else if (c.t === 'fvillage') paintVillage(c.x, c.y, c.s ?? 0, P, o);
  }
  const rb = rnd(3);
  /* Darkened AFTER hazing: far() collapses any pre-haze source toward the same value, so
       the country only separates on a post-haze lightness delta. */
  for (const c of country.filter((c) => c.t === 'cmeadow'))
    o.push(
      `<path d="${polyPath(rectPts(c.x, c.y, c.w, c.h))}" fill="${shade(P.far(P.pasture), 0.16)}"/>`,
    );
  for (let i = 0; i < 14; i++) {
    const x = far.x + rb() * far.w,
      y = far.y + rb() * far.h,
      len = 1.5 + rb() * 4,
      vert = rb() > 0.5;
    const [x1, y1] = project(x, y),
      [x2, y2] = project(vert ? x : x + len, vert ? y + len : y);
    o.push(
      `<line x1="${x1.toFixed(0)}" y1="${y1.toFixed(0)}" x2="${x2.toFixed(0)}" y2="${y2.toFixed(0)}" stroke="${alpha(P.far(P.hedge), 0.45)}" stroke-width="3" stroke-linecap="round"/>`,
    );
  }
  if (stream.length) {
    /* D267: the stream is water in the map's own hand, not a hazed line that reads
               as a path: a darker bank, the water colour, and a lighter ripple down the
               middle. The water is only lightly hazed, so it stays water in either theme. */
    const d = `M${stream
      .map(([x, y]) => {
        const q = project(x, y);
        return `${q[0].toFixed(1)} ${q[1].toFixed(1)}`;
      })
      .join(' L')}`;
    // The token's water, brought towards the map's own brighter water so it reads
    // as water at a glance; the bank is earth, not the grey a road would be.
    const water = mix(P.water, BASE.water, STREAM.waterToBase);
    const bank = mix(shade(P.far(P.pasture), STREAM.bankShade), P.soil, STREAM.bankToSoil);
    o.push(
      `<g data-testid="map-stream" fill="none" stroke-linecap="round" stroke-linejoin="round">` +
        `<path d="${d}" stroke="${bank}" stroke-width="19"/>` +
        `<path d="${d}" stroke="${water}" stroke-width="15"/>` +
        `<path d="${d}" stroke="${alpha(tint(water, STREAM.rippleTint), 0.6)}" stroke-width="6"/>` +
        `<path d="${d}" stroke="${alpha('#ffffff', 0.7)}" stroke-width="1.4" stroke-dasharray="5 11"/>` +
        `</g>`,
    );
  }
  if (lane != null) {
    const l1 = project(far.x, lane),
      l2 = project(far.x + far.w, lane);
    o.push(
      `<line x1="${l1[0].toFixed(0)}" y1="${l1[1].toFixed(0)}" x2="${l2[0].toFixed(0)}" y2="${l2[1].toFixed(0)}" stroke="${P.far(P.road)}" stroke-width="13" stroke-linecap="round"/>`,
    );
  }
  const rt = rnd(21);
  for (const c of country.filter((c) => c.t === 'cwood')) {
    /* Canopy tones are post-haze SHADES of the hazed wood, not independently hazed
         colours: far() pulls every source toward the same haze, which in autumn put the
         topmost lit pass within a hair of the ground and erased the silhouette. */
    // D381: in the 3D country's own greens (`countryLeaf`).
    const w = countryLeaf(P, 'oak', 0.5),
      T = COUNTRY_TREE.tone;
    const dark = shade(w, T.dark),
      midc = shade(w, T.mid),
      lit = tint(w, T.lit);
    const blobs: Array<{ cx: number; cy: number; r: number }> = [];
    for (let i = 0; i < c.w * c.h * 2; i++) {
      const [cx, cy] = project(c.x + 0.3 + rt() * (c.w - 0.6), c.y + 0.3 + rt() * (c.h - 0.6));
      /* The lift is the height of the canopy over its own trunk, and from above a
               canopy sits over the tree rather than above it (D178). */
      blobs.push({ cx, cy: cy - (isPlan() ? 0 : 12), r: 10 + rt() * 8 });
    }
    blobs.sort((a, b) => a.cy - b.cy);
    for (const b of blobs)
      o.push(
        `<circle cx="${(b.cx + 3).toFixed(1)}" cy="${(b.cy + 5).toFixed(1)}" r="${b.r.toFixed(1)}" fill="${dark}"/>`,
      );
    for (const b of blobs)
      o.push(
        `<circle cx="${b.cx.toFixed(1)}" cy="${b.cy.toFixed(1)}" r="${b.r.toFixed(1)}" fill="${midc}"/>`,
      );
    for (const b of blobs)
      o.push(
        `<circle cx="${(b.cx - b.r * 0.22).toFixed(1)}" cy="${(b.cy - b.r * 0.26).toFixed(1)}" r="${(b.r * 0.5).toFixed(1)}" fill="${lit}" opacity=".7"/>`,
      );
  }
}

/**
 * D161: a bank between every two parcels, so one field is told from the next whatever is
 * growing on either. D301: the farm's own edge, not the camera's, which an open piece of land
 * beside it also widens; that land is fenced all the way round, its own island. D331: a hedge
 * decoration grows the farm's own hedges, and the farm gate is in its decoration's paint
 * (D284: as the 3D farm draws it). D344: along the far sides of a kind's outline.
 */
function paintEdges(e: FlatEdges, P: Palette, o: string[]): void {
  const { parcels, outline, lands, hedges, decor } = e;
  if (hedges) paintBoundaries(parcels, P, o);
  const grown = HEDGE_DECOR[decor?.hedge ?? ''];
  const HP = grown ? { ...P, hedge: grown } : P;
  if (outline && hedges)
    for (const [hx0, hy0, hx1, hy1] of outline.hedges) paintHedge(hx0, hy0, hx1, hy1, HP, o);
  if (hedges)
    for (const l of lands) {
      paintHedge(l.x, l.y, l.x + l.w, l.y, HP, o);
      paintHedge(l.x, l.y + l.h, l.x + l.w, l.y + l.h, HP, o);
      paintHedge(l.x, l.y, l.x, l.y + l.h, HP, o);
      paintHedge(l.x + l.w, l.y, l.x + l.w, l.y + l.h, HP, o);
    }
  if (decor?.gateAt)
    paintFarmGate(decor.gateAt, GATE_DECOR[decor.gate ?? ''] ?? TRACK.bridge, TRACK.rail, o);
}

/** D429: a cottage of the module's country on the flat map, hazed back with the country. */
function paintCottage(
  cx: number,
  cy: number,
  w: number,
  d: number,
  h: number,
  P: Palette,
  o: string[],
  wall: string = ASSET_PALETTE.brick,
  roof: string = ASSET_PALETTE.slate,
): void {
  const top = P.S.frost ? mix(roof, '#f4f6f8', 0.7) : roof;
  flatBox(cx - w / 2, cy - d / 2, w, d, 0, h * ZPX, P.far(wall), P.far(top), o);
}

/**
 * D429: the module's village on the hill as the flat map paints it: the church tower and its
 * spire, the hall and six houses, more houses as the world grows (`progress`), the tower
 * windmill past half way and the grain store's silos near the end.
 */
function paintVillage(vx: number, vz: number, progress: number, P: Palette, o: string[]): void {
  const A = FARM_PALETTE;
  paintCottage(vx - 1.3, vz + 0.1, 1.6, 0.8, 0.7, P, o, A.stoneCap, A.slate);
  for (let i = 0; i < 6; i++)
    paintCottage(
      vx + 1.2 + (i % 3),
      vz - 0.8 + Math.floor(i / 3) * 1.3,
      0.8,
      0.55,
      0.45,
      P,
      o,
      i % 2 ? A.brick : A.white,
      i % 3 ? A.slate : A.thatch,
    );
  const extra = Math.round(progress * 14);
  for (let i = 0; i < extra; i++)
    paintCottage(
      vx + 4.2 + (i % 5),
      vz - 1 + Math.floor(i / 5) * 1.25,
      0.8,
      0.55,
      0.45 + (i % 3) * 0.12,
      P,
      o,
      [A.brick, A.white, A.stoneCap][i % 3],
      i % 2 ? A.slate : A.roofRed,
    );
  if (progress > 0.5) paintCottage(vx - 3.6, vz - 1.2, 0.8, 0.8, 2.4, P, o, A.white, A.slate);
  if (progress > 0.8)
    for (let k = 0; k < 4; k++)
      paintCottage(vx + 8 + k * 1.1, vz + 2.4, 0.9, 0.9, 2.6, P, o, A.metal, A.metal);
  // The church tower, and its spire over it.
  paintCottage(vx, vz, 0.9, 0.9, 1.6, P, o, A.stoneCap, A.stoneCap);
  const [sx, sy] = project(vx, vz, 1.7 * ZPX),
    [, ty] = project(vx, vz, 3.1 * ZPX);
  const half = 0.42 * TW * 0.7;
  o.push(
    `<path d="M${(sx - half).toFixed(1)} ${sy.toFixed(1)}L${sx.toFixed(1)} ${ty.toFixed(1)}L${(sx + half).toFixed(1)} ${sy.toFixed(1)}Z" fill="${P.far(A.slate)}"/>`,
  );
}

/** Frost: a fall of snow over the land in use, in winter. */
function paintWeather(far: Bounds, P: Palette, o: string[]): void {
  if (!P.S.frost) return;
  const rf = rnd(5);
  for (let i = 0; i < 120; i++) {
    const [cx, cy] = project(far.x - 1 + rf() * (far.w + 2), far.y - 1 + rf() * (far.h + 2));
    o.push(
      `<circle cx="${cx.toFixed(0)}" cy="${(cy - rf() * 60).toFixed(0)}" r="${(0.8 + rf() * 1.2).toFixed(1)}" fill="#ffffff" opacity=".5"/>`,
    );
  }
}

/* ---------- D422, D427: the asset module's fields, heroes and heart, as both maps place them ---------- */

/** D427: the asset module's crop for each of the farm's eleven crop producers. */
export const FARM_CROP: Readonly<Record<string, string>> = {
  wheat: 'wheat',
  barley: 'barley',
  potatoes: 'potatoes',
  carrots: 'carrots',
  hay: 'hay',
  rapeseed: 'oilseed',
  strawberries: 'strawberries',
  hops: 'hops',
  vines: 'vines',
  saffron: 'saffron',
  watercress: 'watercress',
};

/** D422, D424: a producer's piece from the asset module: its index, level, units and crop. */
export interface FarmAsset {
  i: number;
  level: number;
  units: number;
  /** The module's crop, for a crop producer, whose field fills its own tiles. */
  crop?: string;
}

/**
 * D422, D427, D429: the asset module's piece for a producer's parcel, or null for land with
 * no producer. The trout's hero stands on its pond, which keeps its water.
 */
export function farmAsset(p: IsoParcel): FarmAsset | null {
  if (!p.id || !p.tiles.length) return null;
  const producers = getIdleContent('farm').producers;
  const i = producers.findIndex((q) => q.id === p.id);
  const prod = producers[i];
  if (!prod) return null;
  const units = p.owned ?? 0;
  const level = assetLevel(
    units,
    prod.milestones.map((m) => m.owned),
  );
  const crop = FARM_CROP[p.id];
  return { i, level, units, ...(crop ? { crop } : {}) };
}

/** D422: the heart's form from its drivers, as the module reads them (0, 1 or 2). */
export function farmHeartStage(h: Heart): number {
  const byP = h.producers >= 15 ? 2 : h.producers >= 7 ? 1 : 0;
  const byY = h.years >= 7 ? 2 : h.years >= 3 ? 1 : 0;
  return Math.max(byP, byY);
}

/** The farmhouse in the module's heart at each form: its width, depth and wall height. */
const HEART_HOUSE = { w: [1.5, 1.9, 2.4], d: [1.0, 1.2, 1.5], h: [0.7, 0.85, 1.0] } as const;

/**
 * D427: the top of the heart's chimney in the world (`x`, `y` on the ground, `z` the height in
 * tiles), where the module's `userData.chimney` puts it, for the flat map's smoke.
 */
export function farmHeartChimney(h: Heart): { x: number; y: number; z: number } {
  const plan = heartPlan(h);
  const k = plan.size / 7,
    st = farmHeartStage(h);
  const hw = HEART_HOUSE.w[st] ?? 1.5,
    hd = HEART_HOUSE.d[st] ?? 1,
    hh = HEART_HOUSE.h[st] ?? 0.7;
  return {
    x: plan.x + (3.5 + h.dir * (-1.8 + hw * 0.32)) * k,
    y: plan.y + (3.5 + 1.6 - hd * 0.15) * k,
    z: (hh * 1.4 + 0.35) * k,
  };
}

/**
 * D423: the store's bays beside the heart, one for every storage upgrade, each a small barn,
 * drawn the same in both maps.
 */
export function farmStoreBays(h: Heart): IsoBuildingAt[] {
  return heartPlan(h).bays.map((b) => ({ ...b, kind: 'barn', use: 'storage' }));
}

/** How many screen pixels one tile of height stands, as the other kits' units have it. */
const ZPX = (TW / 2 / Math.SQRT1_2) * Math.cos(Math.PI / 6);

/** A piece of the heart as the flat map paints it, in the module's own frame (7 by 7). */
type HeartPiece =
  | {
      s: 'box';
      x: number;
      z: number;
      w: number;
      d: number;
      y?: number;
      h: number;
      c: string;
      top?: string;
      roof?: boolean;
    }
  | { s: 'cyl'; x: number; z: number; r: number; h: number; c: string }
  | { s: 'ball'; x: number; z: number; r: number; y: number; c: string };

/**
 * D422, D427: the module's farm heart as the flat map paints it, from the same plan as the 3D
 * map's: the verge, its hedge on three sides, the drive, the farmhouse (larger with each form),
 * the great barn and the machinery shed from the second form, the silos and the dairy at the
 * third, and the yard's two trees.
 */
function heartPieces(h: Heart): HeartPiece[] {
  const A = FARM_PALETTE;
  const st = farmHeartStage(h);
  const hw = HEART_HOUSE.w[st] ?? 1.5,
    hd = HEART_HOUSE.d[st] ?? 1,
    hh = HEART_HOUSE.h[st] ?? 0.7;
  const out: HeartPiece[] = [
    { s: 'box', x: 0, z: 0, w: 7, d: 7, h: 0.03, c: A.farmVerge },
    { s: 'box', x: 0.3, z: 0, w: 1.2, d: 7, h: 0.035, c: A.soil },
    { s: 'box', x: 0, z: -3.35, w: 7, d: 0.3, h: 0.42, c: A.hedge },
    { s: 'box', x: -3.35, z: 0, w: 0.3, d: 7, h: 0.42, c: A.hedge },
    { s: 'box', x: 3.35, z: 0, w: 0.3, d: 7, h: 0.42, c: A.hedge },
  ];
  if (st >= 1)
    out.push(
      { s: 'box', x: -1.6, z: -1.9, w: 2, d: 1.2, h: 0.8, c: A.metal, roof: true },
      { s: 'box', x: 1.4, z: -1.2, w: 3.6, d: 2.2, h: 1.4, c: A.roofRed },
      { s: 'box', x: 1.4, z: -1.2, w: 3.7, d: 2.3, y: 1.4, h: 0.5, c: A.metal, roof: true },
    );
  if (st >= 2) {
    out.push({ s: 'box', x: 2.5, z: -2.2, w: 2.2, d: 1.6, h: 1, c: A.white, top: A.metal });
    for (let i = 0; i < 5; i++)
      out.push({ s: 'cyl', x: i * 0.5, z: 2.6 - (i % 2) * 0.1, r: 0.42, h: 3, c: A.metal });
  }
  out.push(
    { s: 'box', x: -1.8, z: 1.6, w: hw, d: hd, h: hh, c: st ? A.brick : A.white },
    { s: 'box', x: -1.8, z: 1.6, w: hw * 1.06, d: hd * 1.1, y: hh, h: hh * 0.45, c: A.slate },
    { s: 'ball', x: -3, z: 2.9, r: 0.42, y: 0.35, c: A.leaf },
    { s: 'ball', x: -0.6, z: 2.9, r: 0.36, y: 0.3, c: A.leaf },
  );
  return out;
}

/** A box on the flat map, its foot `z0` and its height `zh` in screen pixels. */
function flatBox(
  x: number,
  y: number,
  w: number,
  d: number,
  z0: number,
  zh: number,
  wall: string,
  top: string,
  o: string[],
): void {
  if (isPlan()) {
    o.push(`<path d="${polyPath(rectPts(x, y, w, d))}" fill="${top}"/>`);
    return;
  }
  const up = rectPts(x, y, w, d, z0 + zh) as [Point, Point, Point, Point];
  const dn = rectPts(x, y, w, d, z0) as [Point, Point, Point, Point];
  const f = frontCorner(x, y, w, d);
  const l = up[(f + 1) % 4] as Point,
    r = up[(f + 3) % 4] as Point,
    lb = dn[(f + 1) % 4] as Point,
    rb = dn[(f + 3) % 4] as Point,
    fu = up[f] as Point,
    fd = dn[f] as Point;
  if (zh > 0.4) {
    o.push(`<path d="${polyPath([l, fu, fd, lb])}" fill="${shade(wall, 0.18)}"/>`);
    o.push(`<path d="${polyPath([fu, r, rb, fd])}" fill="${shade(wall, 0.06)}"/>`);
  }
  o.push(`<path d="${polyPath(up)}" fill="${top}"/>`);
}

const f1 = (n: number): string => n.toFixed(1);

/** D422, D427: the heart on the flat map, scaled evenly onto the yard and mirrored with it. */
function paintHeart(h: Heart, P: Palette, o: string[]): void {
  const plan = heartPlan(h);
  const k = plan.size / 7,
    m = h.dir;
  const frost = !!P.S.frost;
  const snow = (c: string, on: boolean) => (frost && on ? mix(c, '#f4f6f8', 0.7) : c);
  const wx = (lx: number) => plan.x + (3.5 + m * lx) * k,
    wy = (lz: number) => plan.y + (3.5 + lz) * k;
  const flat = (q: HeartPiece) => q.s === 'box' && q.h < 0.05;
  const pieces = heartPieces(h)
    .map((q) => ({ q, at: project(wx(q.x), wy(q.z))[1] }))
    // The ground first, then back to front.
    .sort((a, b) => Number(flat(b.q)) - Number(flat(a.q)) || a.at - b.at);
  for (const { q } of pieces) {
    if (q.s === 'box') {
      const top = snow(q.top ?? tint(q.c, 0.08), !!q.roof || q.h > 0.3);
      flatBox(
        wx(q.x) - (q.w * k) / 2,
        wy(q.z) - (q.d * k) / 2,
        q.w * k,
        q.d * k,
        (q.y ?? 0) * k * ZPX,
        q.h * k * ZPX,
        q.c,
        top,
        o,
      );
    } else if (q.s === 'cyl') {
      const [bx, by] = project(wx(q.x), wy(q.z), 0);
      const [, ty] = project(wx(q.x), wy(q.z), q.h * k * ZPX);
      const rx = (q.r * k * TW) / Math.SQRT2,
        ry = isPlan() ? rx : rx / 2;
      if (!isPlan())
        o.push(
          `<path d="M${f1(bx - rx)} ${f1(by)} L${f1(bx - rx)} ${f1(ty)} L${f1(bx + rx)} ${f1(ty)} L${f1(bx + rx)} ${f1(by)} A${f1(rx)} ${f1(ry)} 0 0 1 ${f1(bx - rx)} ${f1(by)}Z" fill="${shade(q.c, 0.12)}"/>`,
        );
      o.push(
        `<ellipse cx="${f1(bx)}" cy="${f1(isPlan() ? by : ty)}" rx="${f1(rx)}" ry="${f1(ry)}" fill="${snow(tint(q.c, 0.12), true)}"/>`,
      );
    } else {
      const [cx, cy] = project(wx(q.x), wy(q.z), (q.y + q.r) * k * ZPX);
      const r = q.r * k * TW * 0.7;
      const c = frost ? shade(q.c, 0.35) : q.c;
      o.push(
        `<circle cx="${f1(cx)}" cy="${f1(cy)}" r="${f1(r)}" fill="${c}"/>` +
          `<circle cx="${f1(cx - r * 0.3)}" cy="${f1(cy - r * 0.3)}" r="${f1(r * 0.4)}" fill="${tint(c, 0.12)}"/>`,
      );
    }
  }
  for (const b of farmStoreBays(h))
    paintProp({ k: 'build', x: b.x, y: b.y, w: b.w, h: b.h, kind: 'barn', use: 'storage' }, P, o);
}

/**
 * D422, D427: the gate and its two lanterns at the front of every plot the asset module draws,
 * as its `edges.farm` stands them: two posts, four bars and a lantern on each post.
 */
function paintPlotEdges(parcels: IsoParcel[], P: Palette, o: string[]): void {
  if (isPlan()) return;
  const A = FARM_PALETTE;
  const night = /night|dusk/i.test(P.T.label);
  for (const p of parcels) {
    // The pond has no verge or gate: its water stays.
    if (p.t === 'pond' || !farmAsset(p)) continue;
    const b = unionBounds(p.tiles);
    const y = b.y + b.h - 0.15;
    const posts = [b.x + b.w * 0.38, b.x + b.w * 0.62 + 0.1] as const;
    for (const x of posts) {
      const [px, py] = project(x, y);
      const [, ty] = project(x, y, 0.4 * ZPX);
      o.push(
        `<line x1="${f1(px)}" y1="${f1(py)}" x2="${f1(px)}" y2="${f1(ty)}" stroke="${A.timberDark}" stroke-width="1.6"/>`,
      );
      o.push(
        `<circle cx="${f1(px)}" cy="${f1(ty - 1)}" r="${night ? 2.2 : 1.4}" fill="${A.lampWarm}"/>`,
      );
    }
    for (let i = 0; i < 4; i++) {
      const z = (0.07 + i * 0.08) * ZPX;
      const [ax, ay] = project(posts[0], y, z),
        [bx, by] = project(posts[1], y, z);
      o.push(
        `<line x1="${f1(ax)}" y1="${f1(ay)}" x2="${f1(bx)}" y2="${f1(by)}" stroke="${A.white}" stroke-width="0.9"/>`,
      );
    }
  }
}

/** The farm's flat props: a building that is the heart paints the heart; the rest as before. */
function paintFarmProp(s: Prop, P: Palette, o: string[]): void {
  // D440: in winter every farm animal is indoors, as on the 3D map; the pens stand empty.
  if (s.k === 'animal' && P.S.frost) return;
  if (s.k === 'build') {
    const heart = (s as Prop & { heart?: Heart }).heart;
    if (heart) return paintHeart(heart, P, o);
  }
  paintProp(s, P, o);
}

/** D164, D427: the smoke from the heart's chimney, else the house's, the barn's or a shed's. */
function farmSmoke(buildings: IsoBuildingAt[]): Puff[] {
  const heart = buildings.find((b) => b.heart)?.heart;
  if (heart) {
    const ch = farmHeartChimney(heart);
    const [cx, cy] = project(ch.x, ch.y, ch.z * ZPX + 6);
    return [0, 3.1, 6.2].map((delay, i) => ({
      cx: cx + i * 0.8,
      cy,
      r: SMOKE.r + i * SMOKE.grow,
      delay,
    }));
  }
  const yard =
    buildings.find((b) => b.kind === 'house') ??
    buildings.find((b) => b.kind === 'barn') ??
    buildings.find((b) => b.kind === 'shed');
  return yard ? smokePuffs(yard) : [];
}

/* ---------- the farm's kit ---------- */

/** D348: the farm theme's map kit. */
export const farmKit: MapKit = {
  id: 'farm',
  plots: SLOTS,
  chainBuildings: CHAIN_BUILDINGS,
  landscape: landscapeFor,
  edgeWater: true,
  fieldCycle: FIELD_CYCLE,
  palette,
  phaseTime: (phase) => PHASE_TIME[phase] ?? 'morning',
  light: (phase) => PHASE_LIGHT[phase] ?? PHASE_LIGHT.day!,
  goodWeather: GOOD_WEATHER,
  // D288, D329: the sky behind the farm, which the country fades into with distance.
  sky: (P, phase) => {
    const light = PHASE_LIGHT[phase] ?? PHASE_LIGHT.day!;
    return mix(P.far(P.meadow), light.haze, light.hazeT);
  },
  readyBadge: READY_BADGE,
  // D422, D423: the heart on the yard, the barn's growth its store; each plot's units counted.
  heart: true,
  dress: (p, { owned }) => {
    p.owned = owned;
  },
  // The roofs' height over the ground, which the 3D camera keeps in frame.
  top: () => 2.4,
  tall: false,
  flat: {
    scenery: paintCountry,
    ground: paintParcel,
    fieldJob: paintFieldJob,
    machine: (at, P, o) => paintMachine(0, 0, at.spec.rig, at.maker, P, o),
    edges: paintEdges,
    plotEdges: paintPlotEdges,
    props: propsFor,
    prop: paintFarmProp,
    // D259: what stands on a still map and how tall, for the fit.
    standing: (buildings, parcels) => {
      const out = buildings.map((b) => ({
        x: b.x,
        y: b.y,
        w: b.w,
        h: b.h,
        z: standingHeight(b.kind, b.w),
      }));
      for (const p of parcels) {
        if (!p.mark) continue;
        const b = unionBounds(p.tiles);
        out.push({ ...b, z: standingHeight('mark') });
      }
      return out;
    },
    weather: paintWeather,
    // D164, D427: smoke from the heart's chimney, else the house, the barn or a shed.
    smoke: farmSmoke,
    // D164: light moving on the water of a pond.
    ripples: (parcels) => {
      const out: Ripple[] = [];
      for (const pc of parcels) if (pc.t === 'pond') out.push(...pondRipples(pc.tiles));
      return out;
    },
  },
};
